<div class="wrapper-landing-page">
  <!-- Page Header -->
  <div class="section-title">
    <div class="d-flex align-items-center justify-content-between flex-wrap gap-2">
      <div class="d-flex align-items-center">
        <div class="title">{{ "Landing Page Report" | translate }}</div>
      </div>

      <!-- Quick Action Navigation in Header -->
      <div class="header-actions topbar-actions d-flex align-items-center flex-wrap gap-2">
        <button
          type="button"
          class="btn btn-action-ai ui-classic-only"
          data-ai-purpose="generate summary"
          [disabled]="isLoading || isGeneratingAiSummary"
          (click)="generateAiSummary($event)"
          [title]="'Summarize Landing Page Report with AI Assistant' | translate"
        >
          <i class="fi fi-rr-sparkles mr-1" [class.fa-spin]="isGeneratingAiSummary"></i>
          <span>{{ (isGeneratingAiSummary ? "Generating Summary..." : "AI Summary") | translate }}</span>
          <span class="ai-coin-token" *ngIf="!isGeneratingAiSummary && aiTokenCost" [attr.title]="aiTokenCost + ' tokens'">
            {{ aiTokenCost }}
          </span>
        </button>
        <a
          routerLink="/dashboard/pages"
          class="btn btn-action-secondary ui-classic-only"
        >
          <i class="fi fi-rr-apps mr-1"></i>
          {{ "Manage Pages" | translate }}
        </a>
        <a
          routerLink="/dashboard/page/create"
          class="btn btn-action-primary"
        >
          <i class="fi fi-rr-plus mr-1"></i>
          {{ "Create Landing Page" | translate }}
        </a>
      </div>
    </div>
  </div>

  <div class="section-main mt-4">
    <!-- Purpose & Guidance Description Banner -->
    <div class="report-guide-card mb-4">
      <div class="guide-header d-flex align-items-center mb-2">
        <div class="guide-icon mr-2">
          <i class="fi fi-rr-browser"></i>
        </div>
        <div>
          <h2 class="guide-heading font-weight-bold mb-0">
            {{ "Report Purpose & Insights" | translate }}
          </h2>
        </div>
        <!-- Glass theme: the report actions sit in this card (the page title is in the top bar) -->
        <div class="guide-actions ui-glass-only d-flex align-items-center flex-wrap gap-2 ml-auto">
          <button
            type="button"
            class="btn btn-action-ai"
            data-ai-purpose="generate summary"
            [disabled]="isLoading || isGeneratingAiSummary"
            (click)="generateAiSummary($event)"
            [title]="'Summarize Landing Page Report with AI Assistant' | translate"
          >
            <i class="fi fi-rr-sparkles mr-1" [class.fa-spin]="isGeneratingAiSummary"></i>
            <span>{{ (isGeneratingAiSummary ? "Generating Summary..." : "AI Summary") | translate }}</span>
            <span class="ai-coin-token" *ngIf="!isGeneratingAiSummary && aiTokenCost" [attr.title]="aiTokenCost + ' tokens'">
            {{ aiTokenCost }}
            </span>
          </button>
          <a
            routerLink="/dashboard/pages"
            class="btn btn-action-secondary"
          >
            <i class="fi fi-rr-apps mr-1"></i>
            {{ "Manage Pages" | translate }}
          </a>
        </div>
      </div>
      <p class="intro-text mb-3">
        {{ "Evaluate the conversion efficiency, visitor traffic, and revenue impact of all your published landing pages and external campaigns to optimize marketing ROI and eliminate checkout drop-offs." | translate }}
      </p>
      <div class="guide-pills d-flex flex-wrap gap-2">
        <div class="guide-pill">
          <div class="pill-badge pill-badge-primary mr-2">
            <i class="fi fi-rr-bullseye"></i>
          </div>
          <div class="pill-text">
            <span class="guide-pill-title">{{ "Purpose:" | translate }}</span>
            <span class="guide-pill-desc ml-1">{{ "Measure storefront traffic volume, sales conversions, and revenue generation per landing page." | translate }}</span>
          </div>
        </div>
        <div class="guide-pill">
          <div class="pill-badge pill-badge-success mr-2">
            <i class="fi fi-rr-bulb"></i>
          </div>
          <div class="pill-text">
            <span class="guide-pill-title">{{ "When to Use:" | translate }}</span>
            <span class="guide-pill-desc ml-1">{{ "Use when running ad campaigns, testing landing page designs, auditing funnel drop-offs, and optimizing traffic allocation." | translate }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div *ngIf="isLoading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status"></div>
      <p class="mt-2 text-muted">{{ "Retrieving...please wait" | translate }}</p>
    </div>

    <!-- Failed Loading State -->
    <div class="warn-card mb-3" *ngIf="!isLoading && loadFailed">
      <i class="fi fi-rr-exclamation mr-2"></i>
      <span>{{ "We could not load the landing page report. Please try again." | translate }}</span>
      <button type="button" class="btn btn-retry ml-auto" (click)="loadReport()">
        {{ "Retry" | translate }}
      </button>
    </div>

    <!-- Loaded Content -->
    <div *ngIf="!isLoading && !loadFailed">
      <!-- AI Performance Summary & Insights Card -->
      <div
        class="ai-summary-card mb-4"
        *ngIf="showAiSummaryCard"
      >
        <div class="ai-summary-header d-flex align-items-center justify-content-between flex-wrap gap-2">
          <div class="d-flex align-items-center">
            <div class="ai-icon-badge mr-2">
              <i class="fi fi-rr-sparkles"></i>
            </div>
            <div>
              <div class="ai-title font-weight-bold">
                {{ "AI Performance Summary & Insights" | translate }}
              </div>
              <div class="ai-subtitle text-muted">
                {{ "Automated AI analysis based on current landing page conversion metrics and visitor trends" | translate }}
              </div>
            </div>
          </div>

          <div class="ai-actions d-flex align-items-center gap-2">
            <button
              *ngIf="!isGeneratingAiSummary && aiSummaryContent && !aiSummaryError"
              type="button"
              class="btn btn-sm btn-ai-action"
              (click)="copyAiSummary()"
              [title]="'Copy to Clipboard' | translate"
            >
              <i class="fi" [ngClass]="isCopied ? 'fi-rr-check' : 'fi-rr-copy'"></i>
              <span class="ml-1">{{ (isCopied ? "Copied!" : "Copy") | translate }}</span>
            </button>
            <button
              *ngIf="!isGeneratingAiSummary"
              type="button"
              class="btn btn-sm btn-ai-action"
              data-ai-purpose="generate summary"
              (click)="generateAiSummary($event)"
              [title]="'Regenerate Summary' | translate"
            >
              <i class="fi fi-rr-refresh"></i>
              <span class="ml-1">{{ "Regenerate" | translate }}</span>
              <span class="ai-coin-token ai-coin-sm" *ngIf="aiTokenCost" [attr.title]="aiTokenCost + ' tokens'">
                {{ aiTokenCost }}
              </span>
            </button>
            <button
              type="button"
              class="btn btn-sm btn-ai-close"
              (click)="dismissAiSummary()"
              [attr.aria-label]="'Close' | translate"
            >
              <i class="fi fi-rr-cross"></i>
            </button>
          </div>
        </div>

        <!-- Loading State -->
        <div *ngIf="isGeneratingAiSummary" class="ai-loading-container py-4 text-center">
          <div class="ai-spinner mb-3">
            <i class="fi fi-rr-sparkles fa-spin"></i>
          </div>
          <div class="font-weight-bold text-primary mb-1">
            {{ "Analyzing landing page metrics & generating insights..." | translate }}
          </div>
          <div class="text-muted small">
            {{ "Evaluating conversion funnels, top revenue drivers, and drop-off rates" | translate }}
          </div>
          <div class="ai-skeleton-box mt-3">
            <div class="skeleton-line full"></div>
            <div class="skeleton-line three-quarters"></div>
            <div class="skeleton-line half"></div>
          </div>
        </div>

        <!-- Error State -->
        <div *ngIf="!isGeneratingAiSummary && aiSummaryError" class="ai-error-container p-3 my-2">
          <div class="d-flex align-items-center text-danger mb-2">
            <i class="fi fi-rr-exclamation mr-2"></i>
            <span class="font-weight-bold">{{ aiSummaryError }}</span>
          </div>
          <button type="button" class="btn btn-sm btn-primary mt-1" data-ai-purpose="generate summary" (click)="generateAiSummary($event)">
            {{ "Try Again" | translate }}
          </button>
        </div>

        <!-- AI Content Display -->
        <div
          *ngIf="!isGeneratingAiSummary && !aiSummaryError && aiSummaryContent"
          class="ai-summary-body mt-3"
        >
          <!-- Structured Insights Cards Grid -->
          <div class="ai-insights-grid" *ngIf="parsedSections && parsedSections.length > 0">
            <div
              *ngFor="let sec of parsedSections"
              class="ai-insight-card"
              [ngClass]="'theme-' + sec.theme"
            >
              <div class="ai-card-header">
                <span class="ai-card-badge">
                  <i class="fi" [ngClass]="sec.icon"></i>
                </span>
                <h4 class="ai-card-title">{{ sec.title | translate }}</h4>
              </div>
              <div class="ai-card-body">
                <div *ngFor="let row of sec.rows" class="ai-row-item">
                  <span
                    *ngIf="sec.theme === 'recommendations' && row.index"
                    class="item-num-badge"
                  >{{ row.index }}</span>
                  <span
                    *ngIf="sec.theme !== 'recommendations' || !row.index"
                    class="item-bullet"
                  ></span>
                  <div class="item-content">
                    <span *ngIf="row.label" class="ai-label">{{ row.label }}:</span>
                    <span *ngIf="row.value" class="ai-val-highlight ml-1">{{ row.value }}</span>
                    <span *ngIf="row.note" class="ai-note ml-1"><ng-container *ngIf="row.value">– </ng-container>{{ row.note }}</span>
                    <span *ngIf="row.text && !row.label" class="ai-text">{{ row.text }}</span>
                    <a
                      *ngIf="row.url"
                      [href]="row.url"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="ai-url-pill ml-1"
                      [attr.title]="row.url"
                    >
                      <i class="fi fi-rr-arrow-square-right mr-1"></i>
                      <span>{{ displayUrl(row.url) }}</span>
                    </a>
                    <span *ngIf="row.url && row.text && row.label" class="ai-url-extra ml-1 text-muted">
                      {{ row.text }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Raw Fallback if no sections were parsed -->
          <div *ngIf="!parsedSections || parsedSections.length === 0" class="ai-fallback-text p-3">
            <div class="small text-muted" style="white-space: pre-wrap;">{{ aiSummaryContent }}</div>
          </div>
        </div>
      </div>

      <!-- Summary KPI Metric Cards (5 Columns) -->
      <div class="row summary-row mb-4">
        <!-- Revenue -->
        <div class="col-12 col-sm-6 col-lg mb-3 mb-lg-0">
          <div class="stat-card stat-card-revenue h-100">
            <div class="d-flex justify-content-between align-items-start">
              <div class="stat-label">
                {{ "Revenue" | translate }}
                <button
                  type="button"
                  class="btn-stat-info"
                  #revenueTip="matTooltip"
                  [matTooltip]="STAT_HINTS.revenue | translate"
                  [attr.aria-label]="STAT_HINTS.revenue | translate"
                  matTooltipPosition="above"
                  matTooltipHideDelay="100"
                  (click)="revenueTip.toggle()"
                >
                  <i class="fi fi-rr-info"></i>
                </button>
              </div>
              <div class="stat-icon-wrapper icon-revenue">
                <i class="fi fi-rr-dollar"></i>
              </div>
            </div>
            <div class="stat-value mt-2">{{ formatAmount(summary.revenue) }}</div>
            <div class="stat-subtext mt-1">{{ "Total paid order value" | translate }}</div>
          </div>
        </div>

        <!-- Interaction -->
        <div class="col-12 col-sm-6 col-lg mb-3 mb-lg-0">
          <div class="stat-card stat-card-visitors h-100">
            <div class="d-flex justify-content-between align-items-start">
              <div class="stat-label">
                {{ "Interaction" | translate }}
                <button
                  type="button"
                  class="btn-stat-info"
                  #visitorsTip="matTooltip"
                  [matTooltip]="STAT_HINTS.visitors | translate"
                  [attr.aria-label]="STAT_HINTS.visitors | translate"
                  matTooltipPosition="above"
                  matTooltipHideDelay="100"
                  (click)="visitorsTip.toggle()"
                >
                  <i class="fi fi-rr-info"></i>
                </button>
              </div>
              <div class="stat-icon-wrapper icon-visitors">
                <i class="fi fi-rr-cursor-finger"></i>
              </div>
            </div>
            <div class="stat-value mt-2">{{ formatCount(summary.visitors) }}</div>
            <div class="stat-subtext mt-1">{{ "User actions & engagements" | translate }}</div>
          </div>
        </div>

        <!-- Orders -->
        <div class="col-12 col-sm-6 col-lg mb-3 mb-lg-0">
          <div class="stat-card stat-card-orders h-100">
            <div class="d-flex justify-content-between align-items-start">
              <div class="stat-label">
                {{ "Orders" | translate }}
                <button
                  type="button"
                  class="btn-stat-info"
                  #ordersTip="matTooltip"
                  [matTooltip]="STAT_HINTS.orders | translate"
                  [attr.aria-label]="STAT_HINTS.orders | translate"
                  matTooltipPosition="above"
                  matTooltipHideDelay="100"
                  (click)="ordersTip.toggle()"
                >
                  <i class="fi fi-rr-info"></i>
                </button>
              </div>
              <div class="stat-icon-wrapper icon-orders">
                <i class="fi fi-rr-shopping-bag"></i>
              </div>
            </div>
            <div class="stat-value mt-2">{{ formatCount(summary.orders) }}</div>
            <div class="stat-subtext mt-1">{{ "Paid checkouts completed" | translate }}</div>
          </div>
        </div>

        <!-- Checkout Conversion -->
        <div class="col-12 col-sm-6 col-lg mb-3 mb-lg-0">
          <div class="stat-card stat-card-conversion h-100">
            <div class="d-flex justify-content-between align-items-start">
              <div class="stat-label">
                {{ "Checkout Conversion(%)" | translate }}
                <button
                  type="button"
                  class="btn-stat-info"
                  #conversionTip="matTooltip"
                  [matTooltip]="STAT_HINTS.conversion | translate"
                  [attr.aria-label]="STAT_HINTS.conversion | translate"
                  matTooltipPosition="above"
                  matTooltipHideDelay="100"
                  (click)="conversionTip.toggle()"
                >
                  <i class="fi fi-rr-info"></i>
                </button>
              </div>
              <div class="stat-icon-wrapper icon-conversion">
                <i class="fi fi-rr-chart-pie-alt"></i>
              </div>
            </div>
            <div class="stat-value mt-2">{{ formatRate(summary.conversion) }}</div>
            <div class="stat-subtext mt-1">{{ "Orders / interaction ratio" | translate }}</div>
          </div>
        </div>

        <!-- Abandoned Rate -->
        <div class="col-12 col-sm-6 col-lg">
          <div class="stat-card stat-card-abandoned h-100">
            <div class="d-flex justify-content-between align-items-start">
              <div class="stat-label">
                {{ "Abandoned Rate(%)" | translate }}
                <button
                  type="button"
                  class="btn-stat-info"
                  #abandonedTip="matTooltip"
                  [matTooltip]="STAT_HINTS.abandonedRate | translate"
                  [attr.aria-label]="STAT_HINTS.abandonedRate | translate"
                  matTooltipPosition="above"
                  matTooltipHideDelay="100"
                  (click)="abandonedTip.toggle()"
                >
                  <i class="fi fi-rr-info"></i>
                </button>
              </div>
              <div class="stat-icon-wrapper icon-abandoned">
                <i class="fi fi-rr-shopping-cart-check"></i>
              </div>
            </div>
            <div class="stat-value mt-2">{{ formatRate(summary.abandonedRate) }}</div>
            <div class="stat-subtext mt-1">{{ "Drop-offs without purchase" | translate }}</div>
          </div>
        </div>
      </div>

      <!-- Toolbar: using the exact design from order page -->
      <div class="checkout-toolbar mb-4">
        <div class="filter-options">
          <!-- Sort Dropdown styled as btn-filter from order page -->
          <div ngbDropdown class="d-inline-block">
            <button
              class="btn btn-filter"
              id="dropdownSortFilter"
              ngbDropdownToggle
            >
              <i class="fi fi-rr-settings-sliders mr-1"></i>
              <span>{{ "Sort by" | translate }}: {{ selectedSort.display | translate }}</span>
            </button>
            <div ngbDropdownMenu aria-labelledby="dropdownSortFilter" class="custom-dropdown shadow-md">
              <button
                ngbDropdownItem
                *ngFor="let option of SORT_OPTIONS"
                [class.active]="option.value === selectedSort.value"
                (click)="selectSort(option)"
              >
                {{ option.display | translate }}
              </button>
            </div>
          </div>

          <!-- Direct Export Excel Button -->
          <button
            type="button"
            class="btn btn-filter btn-export-excel"
            (click)="downloadExcel()"
            [disabled]="!filteredRows.length || isExporting"
            title="{{ 'Export to Excel' | translate }}"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="mr-1.5 flex-shrink-0">
              <path fill="#107C41" d="M14 2H6C4.89 2 4 2.89 4 4V20C4 21.11 4.89 22 6 22H18C19.11 22 20 21.11 20 20V8L14 2Z"/>
              <path fill="#FFFFFF" opacity="0.6" d="M14 2L20 8H14V2Z"/>
              <path fill="#FFFFFF" d="M9.6 16.5L11.5 13.6L13.4 16.5H15.5L12.6 12.3L15.3 8.5H13.2L11.5 11L9.8 8.5H7.7L10.4 12.3L7.5 16.5H9.6Z"/>
            </svg>
            <span class="text-[#107C41] font-weight-medium" *ngIf="!isExporting">{{ "Export Excel" | translate }}</span>
            <span class="text-[#107C41] font-weight-medium" *ngIf="isExporting">{{ "Retrieving...please wait" | translate }}</span>
          </button>
        </div>

        <!-- Search Input Box on the right -->
        <div class="search-box">
          <i class="fi fi-rr-search search-icon"></i>
          <input
            type="text"
            class="form-control"
            [(ngModel)]="searchTerm"
            (input)="applyFilters()"
            placeholder="{{ 'Search page URL or product' | translate }}"
            title="{{ 'Search page URL or product' | translate }}"
          />
          <i class="fas fa-times clear-icon" *ngIf="searchTerm" (click)="clearSearch()"></i>
        </div>
      </div>

      <!-- Export Failure Warning -->
      <div class="warn-card mb-3" *ngIf="exportFailed">
        <i class="fi fi-rr-exclamation mr-2"></i>
        <span>{{ "We could not export this report. Please try again." | translate }}</span>
        <button type="button" class="btn btn-retry ml-auto" (click)="downloadExcel()">
          {{ "Retry" | translate }}
        </button>
      </div>

      <!-- Empty states -->
      <div class="empty-state text-center py-5" *ngIf="rows.length === 0">
        <i class="fi fi-rr-file-chart-line"></i>
        <p class="mt-3 text-muted">{{ "No landing page activity recorded yet." | translate }}</p>
      </div>

      <div class="empty-state text-center py-5" *ngIf="rows.length > 0 && filteredRows.length === 0">
        <i class="fi fi-rr-search"></i>
        <p class="mt-3 text-muted">{{ "No landing page matches your search." | translate }}</p>
      </div>

      <!-- Report rows -->
      <div *ngIf="filteredRows.length > 0">
        <!-- Column headers, desktop only -->
        <div class="row report-head d-none d-lg-flex">
          <div class="col-lg-3">{{ "Landing Page" | translate }}</div>
          <div class="col-lg-2">{{ "Products" | translate }}</div>
          <div class="col-lg-1 text-center">{{ "Interaction" | translate }}</div>
          <div class="col-lg-1 text-center">{{ "Orders" | translate }}</div>
          <div class="col-lg-2 text-center">{{ "Revenue" | translate }}</div>
          <div class="col-lg-2 text-center">{{ "Checkout Conversion(%)" | translate }}</div>
          <div class="col-lg-1 text-center">{{ "Abandoned Rate(%)" | translate }}</div>
        </div>

        <div class="card report-card mb-3" *ngFor="let row of pagedRows">
          <div class="row card-items align-items-center">
            <!-- Landing Page URLs -->
            <div class="col-12 col-lg-3 landing-cell">
              <p class="property-text d-lg-none">{{ "Landing Page" | translate }}</p>

              <div class="url-group" *ngFor="let group of row.urlGroups">
                <p class="url-group-label">{{ group.label | translate }}</p>

                <a
                  *ngFor="let url of group.urls | slice: 0 : (group.expanded ? group.urls.length : 1)"
                  [href]="url"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="value-text landing-link"
                  [title]="url"
                >
                  {{ displayUrl(url) }}
                  <i class="fi fi-rr-arrow-small-right ml-1 external-icon"></i>
                </a>

                <button
                  type="button"
                  class="btn-url-more"
                  *ngIf="group.urls.length > 1"
                  (click)="group.expanded = !group.expanded"
                  [attr.aria-expanded]="group.expanded"
                >
                  <i class="fi" [ngClass]="group.expanded ? 'fi-rr-angle-small-up' : 'fi-rr-angle-small-down'"></i>
                  <span *ngIf="!group.expanded">{{ "Show more" | translate }}</span>
                  <span *ngIf="group.expanded">{{ "Show less" | translate }}</span>
                </button>
              </div>

              <p class="value-text mb-0 text-muted" *ngIf="!row.urlGroups.length">-</p>
            </div>

            <!-- Products -->
            <div class="col-12 col-lg-2 product-cell">
              <p class="property-text d-lg-none">{{ "Products" | translate }}</p>

              <div class="product-chips" *ngIf="row.products.length">
                <span
                  class="product-chip"
                  *ngFor="let product of row.products | slice: 0 : (row.productsExpanded ? row.products.length : PRODUCT_PREVIEW)"
                  [title]="productTitle(product)"
                >
                  <i class="fi fi-rr-box mr-1"></i>
                  {{ product.name }}
                </span>
              </div>

              <button
                type="button"
                class="btn-url-more"
                *ngIf="row.products.length > PRODUCT_PREVIEW"
                (click)="row.productsExpanded = !row.productsExpanded"
                [attr.aria-expanded]="row.productsExpanded"
              >
                <i class="fi" [ngClass]="row.productsExpanded ? 'fi-rr-angle-small-up' : 'fi-rr-angle-small-down'"></i>
                <span *ngIf="!row.productsExpanded">+{{ row.products.length - PRODUCT_PREVIEW }}</span>
                <span *ngIf="row.productsExpanded">{{ "Show less" | translate }}</span>
              </button>

              <p class="value-text mb-0 text-muted" *ngIf="!row.products.length">-</p>
            </div>

            <!-- Interaction -->
            <div class="col-6 col-lg-1 text-lg-center">
              <p class="property-text d-lg-none">{{ "Interaction" | translate }}</p>
              <p class="value-text font-weight-bold">{{ formatCount(row.total_visitor) }}</p>
            </div>

            <!-- Orders -->
            <div class="col-6 col-lg-1 text-lg-center">
              <p class="property-text d-lg-none">{{ "Orders" | translate }}</p>
              <p class="value-text font-weight-bold">{{ formatCount(row.total_order) }}</p>
            </div>

            <!-- Revenue -->
            <div class="col-6 col-lg-2 text-lg-center">
              <p class="property-text d-lg-none">{{ "Revenue" | translate }}</p>
              <p class="value-text revenue-text">{{ formatAmount(row.total_revenue) }}</p>
              <div class="meter-bar revenue-bar" [title]="(revenueShare(row) | number:'1.0-1') + '% of peak revenue'">
                <span [style.width.%]="revenueShare(row)"></span>
              </div>
            </div>

            <!-- Checkout Conversion -->
            <div class="col-6 col-lg-2 text-lg-center">
              <p class="property-text d-lg-none">{{ "Checkout Conversion(%)" | translate }}</p>
              <p class="value-text conversion-text">{{ formatRate(row.checkout_conversion) }}</p>
              <div class="meter-bar conversion-bar">
                <span [style.width.%]="clampRate(row.checkout_conversion)"></span>
              </div>
            </div>

            <!-- Abandoned Rate -->
            <div class="col-6 col-lg-1 text-lg-center">
              <p class="property-text d-lg-none">{{ "Abandoned Rate(%)" | translate }}</p>
              <p class="value-text abandoned-text">{{ formatRate(row.abandoned_rate) }}</p>
              <div class="meter-bar abandoned-bar">
                <span [style.width.%]="clampRate(row.abandoned_rate)"></span>
              </div>
            </div>
          </div>
        </div>

        <!-- Pagination Bar -->
        <div class="row px-4 py-3 pagination-row align-items-center" *ngIf="filteredRows.length > pageSize">
          <div class="col-12 col-md-6 d-flex justify-content-center justify-content-md-start mb-2 mb-md-0">
            <ngb-pagination
              [collectionSize]="filteredRows.length"
              [(page)]="currentPage"
              [pageSize]="pageSize"
              aria-label="Landing page report pagination"
              [ellipses]="false"
              [rotate]="true"
              [maxSize]="5"
            ></ngb-pagination>
          </div>
          <div class="col-12 col-md-6 text-center text-md-right">
            <div class="text-muted font-weight-500">
              {{ "Showing" | translate }} <b>{{ start + 1 }}–{{ end }}</b> {{ "of" | translate }}
              <b>{{ filteredRows.length }}</b>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

